import { FieldTemplate, Patient } from 'api/models';
import {
  Condition,
  ConditionGroup,
  ConditionOperation,
  ConditionValueType,
  LogicalOperator,
} from 'api/models/FieldTemplate';
import moment from 'moment';

function areEqual(value1?: string | number, value2?: string | number) {
  if (isNaN(value1 as number) && isNaN(value2 as number)) {
    if (typeof value1 === 'string' && typeof value2 === 'string') {
      return value1.toLowerCase() === value2.toLowerCase();
    }
  }

  return Number(value1) === Number(value2);
}

function areNotEqual(
  value1?: string | number | null,
  value2?: string | number | null,
) {
  if (isNaN(value1 as number) && isNaN(value2 as number)) {
    if (typeof value1 === 'string' && typeof value2 === 'string') {
      return value1.toLowerCase() !== value2.toLowerCase();
    }
  }

  return Number(value1) !== Number(value2);
}

function isDateType(type?: ConditionValueType) {
  return (
    type === ConditionValueType.DATE ||
    type === ConditionValueType.TIME ||
    type === ConditionValueType.DATETIME ||
    type === ConditionValueType.YEAR
  );
}

interface EvaluateDateConditionProps {
  value: any;
  conditionValue: any;
  type?: ConditionValueType;
  operation?: ConditionOperation;
}

function evaluateDateCondition({
  value,
  conditionValue,
  type,
  operation = ConditionOperation.EQUALS,
}: EvaluateDateConditionProps): boolean {
  const momentValue = moment(value);
  const momentConditionValue = moment(conditionValue);

  switch (type) {
    case ConditionValueType.DATE:
      momentValue.startOf('day');
      momentConditionValue.startOf('day');
      break;
    case ConditionValueType.TIME:
      momentValue.format('HH:mm:ss');
      momentConditionValue.format('HH:mm:ss');
      break;
    case ConditionValueType.YEAR:
      momentValue.year();
      momentConditionValue.year();
      break;
    default:
      break;
  }

  switch (operation) {
    case ConditionOperation.EQUALS:
      return momentValue.isSame(momentConditionValue);
    case ConditionOperation.NOT_EQUALS:
      return !momentValue.isSame(momentConditionValue);
    case ConditionOperation.GREATER_THAN:
      return momentValue.isAfter(momentConditionValue);
    case ConditionOperation.GREATER_THAN_OR_EQUAL:
      return momentValue.isSameOrAfter(momentConditionValue);
    case ConditionOperation.LOWER_THAN:
      return momentValue.isBefore(momentConditionValue);
    case ConditionOperation.LOWER_THAN_OR_EQUAL:
      return momentValue.isSameOrBefore(momentConditionValue);
    default:
      return false;
  }
}

interface EvaluateConditionProps {
  condition: Condition;
  values: { [key: string]: any };
  fieldTemplates: FieldTemplate[];
  patient?: Patient;
}

export function evaluateCondition({
  condition,
  values,
  fieldTemplates,
  patient,
}: EvaluateConditionProps): boolean {
  const conditionValue = Array.isArray(condition?.value)
    ? condition?.value
    : // @ts-ignore
    isNaN(condition?.value)
    ? condition?.value
    : Number(condition?.value);
  let value;
  if (condition?.reference.attribute && patient) {
    value = patient[condition?.reference.attribute as keyof Patient];
  }

  if (condition?.reference.alias && values) {
    const field = fieldTemplates.find(
      field => field.alias === condition?.reference.alias,
    );

    if (field) {
      value = values[field.id] ?? null;
    }
  }

  if (value && isDateType(condition?.reference.valueType)) {
    return evaluateDateCondition({
      value,
      conditionValue,
      type: condition?.reference.valueType,
      operation: condition?.operation,
    });
  }

  switch (condition?.operation) {
    case ConditionOperation.EQUALS:
    case ConditionOperation.IS_ANY_OF:
      if (Array.isArray(conditionValue) && !Array.isArray(value)) {
        return conditionValue?.includes(
          value?.toString ? value?.toString() : value,
        );
      }

      if (Array.isArray(conditionValue) && Array.isArray(value)) {
        return conditionValue.find(element => value.includes(element))
          ? true
          : false;
      }

      // this means that the value is empty
      if (!conditionValue && !value) {
        return true;
      }

      return areEqual(value, conditionValue as string);
    case ConditionOperation.NOT_EQUALS:
      if (!(value && !conditionValue)) {
        return false;
      }

      if (Array.isArray(conditionValue)) {
        return !conditionValue?.includes(value as string);
      }

      return areNotEqual(value, conditionValue);
    case ConditionOperation.GREATER_THAN:
      if (condition?.reference.valueType === ConditionValueType.DATE && value) {
        return moment(value).isAfter(moment(conditionValue));
      }

      if (value && conditionValue) {
        return value > conditionValue;
      }

      return false;
    case ConditionOperation.GREATER_THAN_OR_EQUAL:
      if (value && conditionValue) {
        return value >= conditionValue;
      }

      return false;
    case ConditionOperation.LOWER_THAN_OR_EQUAL:
      if (value && conditionValue) {
        return value <= conditionValue;
      }
      return false;
    case ConditionOperation.LOWER_THAN:
      if (value && conditionValue) {
        return value < conditionValue;
      }
      return false;
  }

  return true;
}

interface EvaluateConditionGroupProps {
  rootGroup?: ConditionGroup;
  values: { [key: string]: any };
  fieldTemplates: FieldTemplate[];
  patient?: Patient;
}
export function evaluateConditionGroup({
  rootGroup,
  values,
  fieldTemplates,
  patient,
}: EvaluateConditionGroupProps) {
  if (!rootGroup || !rootGroup?.conditions?.length) {
    return true;
  }

  const booleanArr = rootGroup?.conditions.map(conditionGroup => {
    if (conditionGroup.operation === LogicalOperator.AND) {
      for (const condition of conditionGroup.conditions) {
        if (
          !evaluateCondition({
            condition: condition as Condition,
            values,
            fieldTemplates,
            patient,
          })
        ) {
          return 'false';
        }
      }
      return 'true';
    } else if (conditionGroup.operation === LogicalOperator.OR) {
      for (const condition of conditionGroup.conditions) {
        if (
          evaluateCondition({
            condition: condition as Condition,
            values,
            fieldTemplates,
            patient,
          })
        ) {
          return 'true';
        }
      }
      return 'false';
    } else {
      throw new Error('Invalid condition group operation');
    }
  });

  const logicalString = booleanArr.reduce((res, el, ind) => {
    const op =
      rootGroup.conditions[ind > 0 ? ind - 1 : ind].operation ===
      LogicalOperator.AND
        ? '&&'
        : '||';

    return ind === 0 ? el : `${res} ${op} ${el}`;
  }, '');
  // TODO: fix this
  // eslint-disable-next-line no-eval
  return eval(logicalString);
}
